import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const SAMPLE = `name,sector_code,domain,country
Herman Miller,FURNITURE,hermanmiller.com,US
Vitra,FURNITURE,vitra.com,CH
Muji,FURNITURE,muji.com,JP`;

function fmtDuration(secs) {
  if (!secs || secs < 60) return `${secs || 0}s`;
  const m = Math.round(secs / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function stateColor(s) {
  if (s === "PUBLISHED" || s === "PUBLISHED_ACCESS_RESTRICTED") return "#0F7B3D";
  if (s === "AWAITING_APPROVAL") return "#B97700";
  if (s === "NEEDS_ATTENTION") return "#B00020";
  if (s === "FAILED") return "#B00020";
  if (s === "RUNNING") return "#2A3E56";
  return "var(--bp-text-muted)";
}

function BatchProgress({ batchId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    const load = () => http.get(`/admin/bulk-intake/batches/${batchId}`)
      .then((r) => setData(r.data))
      .catch(() => {});
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [batchId]);
  if (!data) return <div className="text-sm p-8" style={{ color: "var(--bp-text-muted)" }}>Loading batch…</div>;
  const runs = (data.runs || []).filter((r) => filter === "all" || r.state === filter);
  const sc = data.state_counts;
  return (
    <div data-testid="batch-progress">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        {["QUEUED","RUNNING","AWAITING_APPROVAL","NEEDS_ATTENTION","PUBLISHED","FAILED"].map((s) => (
          <button
            key={s}
            onClick={() => setFilter(filter === s ? "all" : s)}
            className={`p-3 border text-left ${filter === s ? "bg-neutral-900 text-white" : "bg-white"}`}
            style={{ borderColor: "var(--bp-border)" }}
            data-testid={`filter-${s}`}
          >
            <div className="text-[10px] uppercase tracking-widest opacity-70">{s.replace(/_/g," ")}</div>
            <div className="font-serif text-3xl" style={{ color: filter === s ? undefined : stateColor(s) }}>{sc[s] || 0}</div>
          </button>
        ))}
      </div>
      <div className="text-xs mb-4" style={{ color: "var(--bp-text-muted)" }}>
        Est. total: {fmtDuration(data.batch.estimated_duration_secs)} · avg run: {fmtDuration(data.batch.avg_recent_duration_secs)}
        · submitted {String(data.batch.submitted_at).slice(0, 16).replace("T", " ")}
      </div>
      <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
        <table className="w-full text-sm">
          <thead><tr className="border-b" style={{ borderColor: "var(--bp-border)" }}>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Brand</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Sector</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>State</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Outcome</th>
            <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>LLM</th>
          </tr></thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`batch-row-${r.brand_id}`}>
                <td className="px-4 py-2 font-serif">{r.brand?.display_name || r.brand_id.slice(0, 8)}</td>
                <td className="px-4 py-2 text-xs font-mono">{r.brand?.sector_code || "—"}</td>
                <td className="px-4 py-2 text-xs font-mono" style={{ color: stateColor(r.state) }}>{r.state}</td>
                <td className="px-4 py-2 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.outcome || "—"}</td>
                <td className="px-4 py-2 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{r.cost_counters?.llm_calls || 0}</td>
              </tr>
            ))}
            {runs.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm" style={{ color: "var(--bp-text-muted)" }}>No runs matching filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AdminBulkIntake() {
  const { batchId } = useParams();
  const [csv, setCsv] = useState("");
  const [notes, setNotes] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batches, setBatches] = useState([]);
  const loadBatches = useCallback(() => {
    http.get("/admin/bulk-intake/batches").then((r) => setBatches(r.data.batches || [])).catch(() => {});
  }, []);
  useEffect(() => { loadBatches(); }, [loadBatches]);

  const validate = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/admin/bulk-intake/validate", { csv });
      if (data.header_errors && data.header_errors.length) {
        toast.error(data.header_errors.join("; "));
        setPreview(null);
        return;
      }
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Validation failed");
    } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!preview || preview.counts.ok === 0) return;
    if (!window.confirm(`Import ${preview.counts.ok} brand(s)? Invalid rows (${preview.counts.invalid || 0}) will be skipped.`)) return;
    setBusy(true);
    try {
      const { data } = await http.post("/admin/bulk-intake/commit", { csv, notes });
      toast.success(`Imported ${data.brands_created} · ${data.runs_enqueued} runs enqueued`, {
        description: `Est. completion: ${fmtDuration(data.estimated_duration_secs)}`,
      });
      setCsv(""); setNotes(""); setPreview(null); loadBatches();
      // Navigate to the batch progress page
      window.location.hash = `#/admin/bulk-intake/${data.batch_id}`;
      window.location.pathname = `/admin/bulk-intake/${data.batch_id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Commit failed");
    } finally { setBusy(false); }
  };

  // Batch progress view
  if (batchId) {
    return (
      <AdminLayout>
        <div className="bp-container py-8">
          <div className="flex items-baseline gap-4 mb-6">
            <Link to="/admin/bulk-intake" className="bp-link text-sm">← All batches</Link>
            <h1 className="font-serif text-3xl">Batch progress</h1>
            <span className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>{batchId.slice(0, 8)}</span>
          </div>
          <BatchProgress batchId={batchId} />
        </div>
      </AdminLayout>
    );
  }

  // Import view
  return (
    <AdminLayout>
      <div className="bp-container py-8" data-testid="admin-bulk-intake">
        <h1 className="font-serif text-4xl mb-2">Bulk add brands</h1>
        <p className="text-sm mb-6" style={{ color: "var(--bp-text-muted)" }}>
          Paste a CSV with columns: <span className="font-mono">name, sector_code, domain, country</span>. Preview validates and dedupes before commit.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--bp-text-muted)" }}>CSV</label>
            <Textarea rows={12} value={csv} onChange={(e) => setCsv(e.target.value)} placeholder={SAMPLE} className="font-mono text-xs" data-testid="csv-input" />
            <div className="mt-2 flex justify-between items-center">
              <button onClick={() => setCsv(SAMPLE)} className="text-xs bp-link" data-testid="paste-sample">Paste sample</button>
              <Button onClick={validate} disabled={busy || !csv.trim()} data-testid="btn-validate">{busy ? "…" : "Validate"}</Button>
            </div>
            <label className="block text-xs uppercase tracking-widest mt-4 mb-1" style={{ color: "var(--bp-text-muted)" }}>Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. Sept 2026 furniture expansion, Nordic brands" data-testid="notes-input" />
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--bp-text-muted)" }}>Preview</div>
            {!preview ? (
              <div className="text-sm p-6 border" style={{ borderColor: "var(--bp-border)", color: "var(--bp-text-muted)" }}>Validate to see the preview.</div>
            ) : (
              <>
                <div className="flex gap-3 mb-3 text-sm">
                  <span className="text-green-800 font-mono" data-testid="preview-ok">OK: {preview.counts.ok || 0}</span>
                  <span className="text-red-800 font-mono" data-testid="preview-invalid">INVALID: {preview.counts.invalid || 0}</span>
                </div>
                <div className="border max-h-96 overflow-auto bg-white" style={{ borderColor: "var(--bp-border)" }}>
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.rows.map((r) => (
                        <tr key={r.row_index} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`preview-row-${r.row_index}`}>
                          <td className="px-2 py-1 font-mono" style={{ color: r.status === "ok" ? "#0F7B3D" : "#B00020", width: 40 }}>{r.status === "ok" ? "OK" : "×"}</td>
                          <td className="px-2 py-1 font-serif">{r.name || <em style={{ color: "var(--bp-text-muted)" }}>—</em>}</td>
                          <td className="px-2 py-1 font-mono">{r.sector_code}</td>
                          <td className="px-2 py-1 font-mono break-all">{r.domain}</td>
                          <td className="px-2 py-1 font-mono" style={{ color: "#B00020" }}>{r.errors.join(", ")}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-3">
                  <Button onClick={commit} disabled={busy || (preview.counts.ok || 0) === 0} data-testid="btn-commit">
                    {busy ? "…" : `Commit ${preview.counts.ok || 0} row(s) & enqueue pipeline`}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-serif text-2xl mb-3">Recent batches</h2>
          <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }} data-testid="batches-list">
            <table className="w-full text-sm">
              <thead><tr className="border-b" style={{ borderColor: "var(--bp-border)" }}>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Submitted</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Rows</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Runs</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Est.</th>
                <th className="px-4 py-2 text-left text-xs uppercase tracking-widest" style={{ color: "var(--bp-text-muted)" }}>Notes</th>
              </tr></thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className="border-b hover:bg-neutral-50" style={{ borderColor: "var(--bp-border)" }}>
                    <td className="px-4 py-2 text-xs font-mono">
                      <Link to={`/admin/bulk-intake/${b.id}`} className="bp-link" data-testid={`batch-link-${b.id}`}>
                        {String(b.submitted_at).slice(0, 16).replace("T", " ")}
                      </Link>
                    </td>
                    <td className="px-4 py-2 text-xs font-mono">{b.row_count_valid} / {b.row_count_total}</td>
                    <td className="px-4 py-2 text-xs font-mono">{b.run_ids?.length || 0}</td>
                    <td className="px-4 py-2 text-xs font-mono">{fmtDuration(b.estimated_duration_secs)}</td>
                    <td className="px-4 py-2 text-xs">{b.notes || <span style={{ color: "var(--bp-text-muted)" }}>—</span>}</td>
                  </tr>
                ))}
                {batches.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-sm" style={{ color: "var(--bp-text-muted)" }}>No batches yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
